import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Film, Box, Clock } from 'lucide-react';
import type { Asset, Shot } from '../types';

export interface DuplicateImportShotCandidate {
  id: string;
  originalCode: string;
  suggestedCode: string;
  occurrence: number;
  imported: number | null;
  calculated: number | null;
  description?: string;
  payload: Shot;
}

export interface DuplicateImportAssetCandidate {
  id: string;
  originalCode: string;
  suggestedCode: string;
  occurrence: number;
  imported: number | null;
  calculated: number | null;
  description?: string;
  payload: Asset;
}

export interface ImportDiagnosticsDoc {
  docId: string;
  docName: string;
  type: 'shots' | 'assets' | 'key' | 'top_sheet';
  parsedRows: number;
  validRows: number;
  importedRows: number;
  duplicateRows: number;
  thumbnailMatches?: number;
}

export interface ImportDiagnosticsSummary {
  totalParsedShotRows: number;
  totalValidShotRows: number;
  totalImportedShots: number;
  totalDuplicateShots: number;
  totalParsedAssetRows: number;
  totalValidAssetRows: number;
  totalImportedAssets: number;
  totalDuplicateAssets: number;
  docs: ImportDiagnosticsDoc[];
}

export interface ImportOutcomeStep {
  label: string;
  detail: string;
  status: 'done' | 'warning' | 'info';
}

export interface ImportOutcomeSummary {
  mode: 'import' | 'merge';
  sourceDocs: string[];
  steps: ImportOutcomeStep[];
}

export interface ImportProgressData {
  isOpen: boolean;
  isComplete: boolean;
  isPreparing?: boolean;
  phaseLabel?: string | null;
  totalShots: number;
  processedShots: number;
  totalAssets: number;
  processedAssets: number;
  totalThumbnails: number;
  processedThumbnails: number;
  onsetDays: number;
  bidDate: string | null;
  version: string | null;
  priceAccuracy?: {
    overall?: { accuracy: number | null; imported: number | null; calculated: number | null };
    shots?: { accuracy: number | null; imported: number | null; calculated: number | null };
    assets?: { accuracy: number | null; imported: number | null; calculated: number | null };
    onset?: { accuracy: number | null; imported: number | null; calculated: number | null };
  };
  comparison?: {
    onset?: {
      importedDays: number | null;
      calculatedDays: number | null;
      importedTotal: number | null;
      calculatedTotal: number | null;
    };
    episodes?: Array<{
      episode: string;
      imported: number | null;
      calculated: number | null;
      variance: number | null;
      shotCount: number;
    }>;
    assets?: {
      importedTotal: number | null;
      calculatedTotal: number | null;
      rows: Array<{
        code: string;
        imported: number | null;
        calculated: number | null;
        variance: number | null;
      }>;
    };
    shots?: Array<{
      shotCode: string;
      imported: number | null;
      calculated: number | null;
      variance: number | null;
      episode: string;
    }>;
  };
  duplicateConflicts?: {
    shots: DuplicateImportShotCandidate[];
    assets: DuplicateImportAssetCandidate[];
  };
  diagnostics?: ImportDiagnosticsSummary;
  outcomeSummary?: ImportOutcomeSummary;
}

interface Props {
  progress: ImportProgressData | null;
  onClose: () => void;
}

export function ImportProgressModal({ progress, onClose }: Props) {
  if (!progress || !progress.isOpen || progress.isComplete) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col"
        >
          <div className="p-6 border-b border-slate-100 bg-slate-50/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm bg-indigo-100 text-indigo-600">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">
                  {progress.isPreparing ? "Preparing Import..." : "Importing Data..."}
                </h2>
                <p className="text-sm text-slate-500">
                  {progress.isPreparing
                    ? "Reading data from Google Sheets..."
                    : "Please wait while we process your documents."}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6 flex-1 overflow-y-auto">
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="w-14 h-14 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                {progress.isPreparing ? <Loader2 className="w-7 h-7 animate-spin" /> : <Clock className="w-7 h-7" />}
              </div>
              <div className="space-y-2 text-center">
                <p className="text-sm font-medium text-slate-700">
                  {progress.isPreparing ? 'Preparing quote import' : 'Import in progress'}
                </p>
                <p className="text-sm text-slate-500">
                  {progress.isPreparing
                    ? 'Reading the legacy quote and setting up the import.'
                    : 'Importing shots, assets, rates, and quote reference data. This can take a moment.'}
                </p>
                {progress.phaseLabel ? (
                  <p className="text-xs font-medium text-indigo-600">
                    Current phase: {progress.phaseLabel}
                  </p>
                ) : null}
              </div>
              <div className="grid w-full grid-cols-2 gap-4 pt-2">
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                  <div className="flex items-center gap-2 text-xs font-medium text-slate-500 mb-1">
                    <Film className="w-3.5 h-3.5" />
                    Shot Sheets
                  </div>
                  <div className="text-sm font-bold text-slate-900">{progress.totalShots}</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                  <div className="flex items-center gap-2 text-xs font-medium text-slate-500 mb-1">
                    <Box className="w-3.5 h-3.5" />
                    Asset Sheets
                  </div>
                  <div className="text-sm font-bold text-slate-900">{progress.totalAssets}</div>
                </div>
              </div>
              {progress.diagnostics ? (
                <div className="grid w-full grid-cols-2 gap-4">
                  <div className="bg-sky-50 rounded-xl p-3 border border-sky-100 text-left">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-700 mb-2">Shot Diagnostics</div>
                    <div className="space-y-1 text-xs text-slate-700">
                      <div>Parsed: <span className="font-semibold">{progress.diagnostics.totalParsedShotRows}</span></div>
                      <div>Valid: <span className="font-semibold">{progress.diagnostics.totalValidShotRows}</span></div>
                      <div>Imported: <span className="font-semibold">{progress.diagnostics.totalImportedShots}</span></div>
                      <div>Duplicates: <span className="font-semibold">{progress.diagnostics.totalDuplicateShots}</span></div>
                    </div>
                  </div>
                  <div className="bg-sky-50 rounded-xl p-3 border border-sky-100 text-left">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-700 mb-2">Asset Diagnostics</div>
                    <div className="space-y-1 text-xs text-slate-700">
                      <div>Parsed: <span className="font-semibold">{progress.diagnostics.totalParsedAssetRows}</span></div>
                      <div>Valid: <span className="font-semibold">{progress.diagnostics.totalValidAssetRows}</span></div>
                      <div>Imported: <span className="font-semibold">{progress.diagnostics.totalImportedAssets}</span></div>
                      <div>Duplicates: <span className="font-semibold">{progress.diagnostics.totalDuplicateAssets}</span></div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
